/* eslint-disable max-lines */
import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Play, Loader2, RotateCcw, AlertCircle, RefreshCw, Code2
} from 'lucide-react';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    Label
} from 'shared/components/ui/label';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import {
    useOpcodeStream
} from './useOpcodeStream';
import NdjsonEventRenderer from './NdjsonEventRenderer';
import TypedField, {
    coerceTypedFieldValue
} from './TypedField';

/**
 * OpcodeTestPanel — form-driven opcode tester.
 *
 * Schema-driven (UF-13). Fetches the full opcode via
 * jsonOpcodeService.getJSONOpcodeById and renders each `inputs[i]` as a
 * TypedField inheriting type / required / constraint / description /
 * default. On Run streams CPIE / NDJSON events through useOpcodeStream.
 *
 * Live reflection: a Refresh button next to the picker re-fetches the
 * opcode (so edits made in the OpCode editor surface here without a
 * full nav). Auto-refetch fires on mount + whenever the opcodeId or
 * the parent's expandedOpcode changes.
 *
 * Three contexts:
 *   1. AssistantWorkspace — pass `assistant`. Picker is over the
 *      assistant's `opcodes[]`.
 *   2. AssistantWorkspace inline editor — pass `expandedOpcode`. Auto-
 *      selected; picker is hidden.
 *   3. Library OpCode editor — pass `lockedOpcode={{opcode_id, title?}}`. No
 *      picker; the panel is pinned to that single opcode and only the
 *      Refresh affordance can re-fetch its definition.
 *
 * Optional `opcodeMeta` — map of opcode_id → { title } from useOpcodeMeta
 * (assistant workspace) so the picker shows names without extra fetches.
 *
 * Layout:
 *   - Default (narrow): inputs on top, stream below.
 *   - Maximized: inputs left (440px), stream right (flex-1). 2-pane.
 */
export default function OpcodeTestPanel({
    assistant,
    expandedOpcode,
    lockedOpcode,
    opcodeMeta,
    maximized = false,
    isDirty = false,
    onSaveFirst,
    refreshSignal = 0
}) {
    const {
        oragID, subOragID
    } = useParams();
    const lockedId = lockedOpcode?.opcode_id || lockedOpcode?.id || '';
    const isLocked = !!lockedId;
    const pinnedOpcode = isLocked || !!expandedOpcode;

    const opts = useMemo(
        () => (assistant?.opcodes || []).map((o) => o.opcode_id).filter(Boolean),
        [assistant]
    );

    const [titleById, setTitleById] = useState({});
    const fetchedTitleIdsRef = useRef(new Set());

    const [opcodeId, setOpcodeId] = useState(
        lockedId || expandedOpcode?.opcode_id || expandedOpcode?.id || opts[0] || ''
    );
    const [opcode, setOpcode] = useState(expandedOpcode || null);
    const [loadingOpcode, setLoadingOpcode] = useState(false);
    const [opcodeError, setOpcodeError] = useState('');
    const [inputs, setInputs] = useState({});
    // Stable memory_log_id per session: regenerated whenever the selected opcode changes.
    // Only used when the opcode has ≥1 Memory step (engine enforces this requirement).
    const memoryLogId = useRef(`dock-${Date.now()}`);
    // refreshTick bumps to force a re-fetch even when opcodeId hasn't changed
    // (used by the Refresh button after the user edits the opcode elsewhere).
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        // lockedOpcode wins — the parent owns the choice in this mode.
        if (isLocked) {
            if (lockedId && lockedId !== opcodeId) setOpcodeId(lockedId);
            return;
        }
        const next = expandedOpcode?.opcode_id || expandedOpcode?.id;
        if (next && next !== opcodeId) setOpcodeId(next);
    }, [isLocked, lockedId, expandedOpcode, opcodeId]);

    const fetchOpcode = useCallback(() => setRefreshTick((t) => t + 1), []);

    // Parent bumps refreshSignal after inline editor save — re-fetch definition + inputs.
    useEffect(() => {
        if (refreshSignal > 0) setRefreshTick((t) => t + 1);
    }, [refreshSignal]);

    useEffect(() => {
        let cancelled = false;
        if (!opcodeId || !oragID || !subOragID) {
            setOpcode(null);
            return undefined;
        }
        const expandedMatches = expandedOpcode
            && (expandedOpcode.opcode_id === opcodeId || expandedOpcode.id === opcodeId)
            && Array.isArray(expandedOpcode.inputs);
        // Skip the network fetch if the parent supplied a fresh, fully-shaped
        // opcode AND the user hasn't asked for an explicit refresh.
        if (expandedMatches && refreshTick === 0) {
            setOpcode(expandedOpcode);
            return undefined;
        }
        setLoadingOpcode(true);
        setOpcodeError('');
        jsonOpcodeService.getJSONOpcodeById({
            organizationId: oragID,
            subOrganizationId: subOragID,
            opcodeId
        })
            .then((res) => {
                if (cancelled) return;
                const doc = res?.data || res;
                setOpcode(doc || null);
            })
            .catch((err) => {
                if (cancelled) return;
                setOpcodeError(err?.response?.data?.message || err?.message || 'Failed to load opcode');
                setOpcode(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingOpcode(false);
            });
        return () => { cancelled = true; };
    }, [opcodeId, oragID, subOragID, expandedOpcode, refreshTick]);

    // Titles for picker / pinned header — parent meta, then local prefetch cache.
    useEffect(() => {
        let cancelled = false;
        const ids = pinnedOpcode
            ? [lockedId || expandedOpcode?.opcode_id || expandedOpcode?.id].filter(Boolean)
            : opts;
        if (!ids.length || !oragID || !subOragID) return undefined;

        const needFetch = ids.filter((id) => {
            if (fetchedTitleIdsRef.current.has(id)) return false;
            const fromMeta = opcodeMeta?.[id]?.title;
            const fromExpanded = expandedOpcode
                && (expandedOpcode.opcode_id === id || expandedOpcode.id === id)
                && expandedOpcode.title;
            const fromLocked = lockedId === id && lockedOpcode?.title;
            return !fromMeta && !fromExpanded && !fromLocked;
        });
        if (!needFetch.length) return undefined;

        needFetch.forEach((id) => fetchedTitleIdsRef.current.add(id));

        Promise.all(needFetch.map((id) =>
            jsonOpcodeService.getJSONOpcodeById({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcodeId: id
            }).then((res) => {
                const doc = res?.data || res;
                return [id, doc?.title || id];
            }).catch(() => [id, id])
        )).then((pairs) => {
            if (cancelled) return;
            setTitleById((prev) => {
                const next = { ...prev };
                for (const [id, title] of pairs) next[id] = title;
                return next;
            });
        });
        return () => { cancelled = true; };
    }, [opts, pinnedOpcode, lockedId, expandedOpcode, lockedOpcode, opcodeMeta, oragID, subOragID, refreshTick]);

    useEffect(() => {
        fetchedTitleIdsRef.current = new Set();
    }, [refreshTick, oragID, subOragID]);

    function opcodeTitle(id) {
        if (!id) return '';
        if (opcode && (opcode.opcode_id === id || opcode.id === id) && opcode.title) {
            return opcode.title;
        }
        if (expandedOpcode
            && (expandedOpcode.opcode_id === id || expandedOpcode.id === id)
            && expandedOpcode.title) {
            return expandedOpcode.title;
        }
        if (lockedId === id && lockedOpcode?.title) return lockedOpcode.title;
        if (opcodeMeta?.[id]?.title) return opcodeMeta[id].title;
        if (titleById[id]) return titleById[id];
        return id;
    }

    // Rotate memory_log_id whenever the selected opcode changes so distinct
    // runs don't accidentally share the same memory context.
    useEffect(() => { memoryLogId.current = `dock-${Date.now()}`; }, [opcodeId]);

    // Reset input map whenever opcode shape changes.
    useEffect(() => {
        const decl = Array.isArray(opcode?.inputs) ? opcode.inputs : [];
        setInputs((prev) => {
            const next = {};
            for (const f of decl) {
                const name = typeof f === 'string' ? f : (f?.name || '');
                if (!name) continue;
                // Preserve existing value if the field still exists; otherwise
                // seed with the declared default (if any).
                if (prev[name] !== undefined && prev[name] !== '') {
                    next[name] = prev[name];
                } else {
                    next[name] = (f && typeof f === 'object' && f.default != null)
                        ? String(f.default)
                        : '';
                }
            }
            return next;
        });
    }, [opcode]);

    // A memory step can live in the top-level steps, in definitions, inside a
    // flow's steps, or nested in a parallel step's branches — scan all of them.
    // Missing any of these meant we skipped sending memory_log_id and the engine
    // then ran the memory step with no session id.
    const hasMemoryStep = useMemo(() => {
        const isMem = (s) => s?.kind === 'memory' || s?.type === 'memory';
        const scan = (list) => Array.isArray(list) && list.some((s) => {
            if (isMem(s)) return true;
            const branches = s?.branches || s?.config?.branches;
            return scan(branches);
        });
        if (!opcode) return false;
        if (scan(opcode.steps) || scan(opcode.definitions)) return true;
        return Array.isArray(opcode.flows) && opcode.flows.some((f) => scan(f?.steps));
    }, [opcode]);

    const inputDecls = useMemo(() => {
        const decl = Array.isArray(opcode?.inputs) ? opcode.inputs : [];
        return decl.map((f) => (typeof f === 'string'
            ? {
                name: f,
                type: 'string'
            }
            : {
                ...f,
                type: f?.type || 'string'
            }
        ));
    }, [opcode]);

    const stream = useOpcodeStream({
        orgId: oragID,
        subOrgId: subOragID
    });

    const setCount = useMemo(
        () => Object.values(inputs).filter((v) => v !== '' && v != null).length,
        [inputs]
    );

    function handleChange(name, value) {
        setInputs((prev) => ({
            ...prev,
            [name]: value
        }));
    }

    function clearInputs() {
        setInputs((prev) => {
            const reset = {};
            for (const k of Object.keys(prev)) reset[k] = '';
            return reset;
        });
    }

    function handleRun() {
        if (!opcodeId || stream.isRunning) return;
        const coerced = {};
        for (const f of inputDecls) {
            const raw = inputs[f.name];
            if (raw === '' || raw == null) continue;
            coerced[f.name] = coerceTypedFieldValue(raw, f.type);
        }
        stream.run(opcodeId, coerced, hasMemoryStep ? {
            memoryLogId: memoryLogId.current
        } : {});
    }

    const noOpcodes = !isLocked && opts.length === 0 && !expandedOpcode;

    return (
        <div className={cn(
            'flex h-full overflow-hidden bg-card/30 relative',
            maximized ? 'flex-col lg:flex-row' : 'flex-col'
        )}>
            {/* ── UNSAVED BLOCKER ───────────────────────────────── */}
            {isDirty && (
                <UnsavedBlocker
                    onSaveFirst={onSaveFirst}
                    message={isLocked
                        ? 'Save this opcode before running a test so the engine picks up your latest definition.'
                        : undefined}
                />
            )}
            {/* ── INPUTS PANE ─────────────────────────────────── */}
            <div className={cn(
                'flex flex-col min-h-0',
                maximized ? 'lg:w-[440px] lg:flex-shrink-0 lg:border-r' : 'border-b max-h-[55%]'
            )}>
                {/* Picker + opcode card header */}
                <div className="flex-shrink-0 border-b bg-card px-3 py-2.5 space-y-2">
                    {noOpcodes && (
                        <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[12px] text-muted-foreground">
                            This assistant has no opcodes yet. Add one in the <b>Behaviors</b> section first.
                        </div>
                    )}
                    {!noOpcodes && !pinnedOpcode && (
                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <Label className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                                    OpCode
                                </Label>
                                <button
                                    type="button"
                                    onClick={fetchOpcode}
                                    disabled={loadingOpcode || !opcodeId}
                                    title="Re-fetch opcode (after editing)"
                                    className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground hover:text-primary disabled:opacity-50"
                                >
                                    <RefreshCw className={cn('h-3 w-3', loadingOpcode && 'animate-spin')} />
                                    Refresh
                                </button>
                            </div>
                            <Select value={opcodeId} onValueChange={setOpcodeId}>
                                <SelectTrigger className="h-8 text-[12.5px]">
                                    <SelectValue placeholder="Pick an opcode" />
                                </SelectTrigger>
                                <SelectContent>
                                    {opts.map((id) => (
                                        <SelectItem key={id} value={id}>
                                            <span className="truncate text-[12.5px] font-medium">
                                                {opcodeTitle(id)}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {pinnedOpcode && (
                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <Label className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                                    OpCode
                                </Label>
                                <button
                                    type="button"
                                    onClick={fetchOpcode}
                                    disabled={loadingOpcode || !opcodeId}
                                    title="Re-fetch opcode (after saving edits)"
                                    className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground hover:text-primary disabled:opacity-50"
                                >
                                    <RefreshCw className={cn('h-3 w-3', loadingOpcode && 'animate-spin')} />
                                    Refresh
                                </button>
                            </div>
                            <div
                                className="rounded-md border bg-muted/30 px-2.5 py-1.5 truncate text-[12.5px] font-semibold text-foreground"
                                title={opcodeId}
                            >
                                {opcodeTitle(opcodeId) || opcodeId}
                            </div>
                        </div>
                    )}

                    {opcodeError && (
                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[11.5px] text-destructive">
                            <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                            <span>{opcodeError}</span>
                        </div>
                    )}

                    {loadingOpcode && (
                        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" /> loading opcode…
                        </div>
                    )}

                    {!loadingOpcode && opcode && (
                        <div className="rounded-md border bg-muted/30 px-2.5 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-[12.5px] font-semibold text-foreground">
                                        {opcode.title || opcodeTitle(opcode.opcode_id || opcode.id)}
                                    </div>
                                    {opcode.description && (
                                        <div className="line-clamp-2 break-words text-[11px] text-muted-foreground">
                                            {opcode.description}
                                        </div>
                                    )}
                                </div>
                                {opcode.version && (
                                    <span className="flex-shrink-0 rounded-full border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                                        v{opcode.version}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Typed inputs */}
                {!loadingOpcode && opcode && (
                    <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
                        <div className="flex items-center justify-between border-b border-dashed pb-1">
                            <h4 className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                                <Code2 className="h-3 w-3" /> Typed inputs
                            </h4>
                            <span className="font-mono text-[10px] text-muted-foreground">
                                {setCount}/{inputDecls.length}
                            </span>
                        </div>

                        {inputDecls.length === 0 ? (
                            <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11.5px] text-muted-foreground">
                                This opcode declares no inputs. Click <b>Run</b> to execute it as-is.
                            </div>
                        ) : (
                            <div className="space-y-2.5">
                                {inputDecls.map((f) => (
                                    <TypedField
                                        key={f.name}
                                        name={f.name}
                                        type={f.type}
                                        required={!!f.required}
                                        description={f.description}
                                        constraint={f.constraint}
                                        value={inputs[f.name] ?? ''}
                                        onChange={(v) => handleChange(f.name, v)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Memory log ID — shown only when the opcode has a Memory step */}
                {!loadingOpcode && opcode && hasMemoryStep && (
                    <div className="flex-shrink-0 border-t bg-muted/20 px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                                memory_log_id
                            </span>
                            <span className="font-mono text-[10.5px] text-muted-foreground select-all">
                                {memoryLogId.current}
                            </span>
                        </div>
                    </div>
                )}

                {/* Action bar */}
                <div className="flex-shrink-0 border-t bg-background px-3 py-2 flex items-center justify-end gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={clearInputs}
                        disabled={stream.isRunning || setCount === 0}
                    >
                        <RotateCcw className="mr-1 h-3 w-3" /> Clear
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleRun}
                        disabled={!opcodeId || stream.isRunning || !opcode}
                    >
                        {stream.isRunning
                            ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            : <Play className="mr-1 h-3 w-3" />}
                        Run
                    </Button>
                </div>
            </div>

            {/* ── STREAM PANE ────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-muted/10">
                {stream.events.length === 0 && !stream.isRunning && !stream.error && (
                    <div className="text-sm text-muted-foreground">
                        Fill the inputs and click <b>Run</b>. Live events stream here:
                        <span className="ml-1 font-mono text-[11px]">begin → step → delta → result</span>.
                    </div>
                )}
                <NdjsonEventRenderer
                    events={stream.events}
                    mode="opcode"
                    taskId={stream.taskId}
                    error={stream.error}
                    isRunning={stream.isRunning}
                />
            </div>
        </div>
    );
}

export function UnsavedBlocker({
    onSaveFirst,
    message = 'Save the assistant before running a test so the engine picks up your latest config.'
}) {
    const [saving, setSaving] = React.useState(false);
    async function handle() {
        setSaving(true);
        try { await onSaveFirst?.(); } finally { setSaving(false); }
    }
    return (
        <div className="absolute inset-0 z-20 bg-background/50">
            <div className="absolute inset-x-0 bottom-0 border-t bg-card px-3 py-3 shadow-sm">
                <div className="flex items-center gap-3">
                    <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-snug">Unsaved changes</p>
                        <p className="text-xs text-muted-foreground leading-snug">{message}</p>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        className="shrink-0"
                        onClick={handle}
                        disabled={saving}
                    >
                        {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
